import type { BlogArticle } from '../types/blog'
import { queryPublishedArticles } from './useBlogContent'

interface RelatedArticlesOptions {
  minSharedTags?: number
  maxResults?: number
}

const DEFAULT_MIN_SHARED_TAGS = 2
const DEFAULT_MAX_RESULTS = 3
const MIN_RESULTS = 3
const MAX_RESULTS = 5

function normalizeTag(tag: string): string {
  return tag.trim().toLowerCase()
}

function toTagSet(tags: string[]): Set<string> {
  return new Set(tags.map(normalizeTag).filter(Boolean))
}

function countSharedTags(current: Set<string>, candidateTags: string[]): number {
  const candidate = toTagSet(candidateTags)
  let shared = 0
  for (const tag of candidate) {
    if (current.has(tag)) shared += 1
  }
  return shared
}

function toTimestamp(value: string): number {
  const timestamp = new Date(value).getTime()
  return Number.isNaN(timestamp) ? 0 : timestamp
}

function resolveMaxResults(maxResults?: number): number {
  if (typeof maxResults !== 'number' || !Number.isFinite(maxResults)) return DEFAULT_MAX_RESULTS
  const rounded = Math.trunc(maxResults)
  return Math.min(MAX_RESULTS, Math.max(MIN_RESULTS, rounded))
}

function resolveMinSharedTags(minSharedTags?: number): number {
  if (typeof minSharedTags !== 'number' || !Number.isFinite(minSharedTags)) return DEFAULT_MIN_SHARED_TAGS
  return Math.max(1, Math.trunc(minSharedTags))
}

export async function getRelatedArticles(
  currentArticle: Pick<BlogArticle, 'path' | 'slug' | 'tags'>,
  options: RelatedArticlesOptions = {},
): Promise<BlogArticle[]> {
  const currentTags = toTagSet(Array.isArray(currentArticle.tags) ? currentArticle.tags : [])
  if (currentTags.size === 0) return []

  const maxResults = resolveMaxResults(options.maxResults)
  const minSharedTags = resolveMinSharedTags(options.minSharedTags)

  const articles = await queryPublishedArticles()
  const seen = new Set<string>()

  return articles
    .filter((candidate) => candidate.path !== currentArticle.path && candidate.slug !== currentArticle.slug)
    .filter((candidate) => {
      const dedupeKey = `${candidate.path}|${candidate.slug}`
      if (seen.has(dedupeKey)) return false
      seen.add(dedupeKey)
      return true
    })
    .map((candidate) => ({
      article: candidate,
      sharedTagCount: countSharedTags(currentTags, candidate.tags),
    }))
    .filter(({ sharedTagCount }) => sharedTagCount >= minSharedTags)
    .sort((a, b) => {
      if (b.sharedTagCount !== a.sharedTagCount) return b.sharedTagCount - a.sharedTagCount
      const publishedDelta = toTimestamp(b.article.publishedAt) - toTimestamp(a.article.publishedAt)
      if (publishedDelta !== 0) return publishedDelta
      return a.article.path.localeCompare(b.article.path)
    })
    .slice(0, maxResults)
    .map(({ article }) => article)
}

export function useRelatedArticles() {
  return {
    getRelatedArticles,
  }
}
